/**
 * Date Utilities for the main WaWi app
 *
 * Backend stores all timestamps in UTC without timezone suffix
 * (e.g. "2024-01-15 09:00:00" instead of "2024-01-15 09:00:00Z").
 * JavaScript's `new Date("2024-01-15 09:00:00")` treats this as LOCAL time,
 * causing all times to display incorrectly.
 *
 * These utilities correctly parse UTC timestamps and format them
 * in the timezone of the COMPANY (`tenant_settings.general.timezone`).
 *
 * 🚨 Massgeblich ist die Firma, nicht der Betrachter (Stand 19.08.2026): Der Tagesfilter der
 * Listen grenzt serverseitig in der Firmen-Zone ab. Wuerde die Anzeige der persoenlichen
 * Einstellung folgen, saehe ein Benutzer in einer anderen Zone Zeiten, die nicht zu dem Tag
 * passen, den er gefiltert hat. Die persoenliche Zone bleibt als Rueckfallebene.
 */

import { useAuthStore } from '../store/useAuthStore';

const LOCALE = 'de-DE';
const DEFAULT_TIMEZONE = 'Europe/Berlin';

/**
 * Zeitzone fuer die Anzeige: Firma → persoenliche Einstellung → Europe/Berlin.
 */
function getDisplayTimezone(): string {
  const user = useAuthStore.getState().user;
  return user?.tenantTimezone || user?.timezone || DEFAULT_TIMEZONE;
}

/**
 * Parse a backend timestamp (UTC, no timezone suffix) into a Date object.
 *
 * Nimmt bewusst auch Zahlen und Date-Objekte entgegen: Die Formatierer werden aus dem gesamten
 * Frontend aufgerufen, und ein Unix-Zeitstempel oder ein bereits gebautes Date darf nicht in einen
 * Laufzeitfehler laufen (`dateString.endsWith is not a function`) — er wird einfach durchgereicht.
 */
export function parseUTC(dateString: string | number | Date): Date {
  if (!dateString) return new Date();
  if (dateString instanceof Date) return dateString;
  if (typeof dateString === 'number') return new Date(dateString);
  // Already has timezone info → use as-is
  if (dateString.endsWith('Z') || /[+-]\d{2}(:\d{2})?$/.test(dateString)) {
    return new Date(dateString);
  }
  // Append 'Z' so JS interprets as UTC
  return new Date(dateString.replace(' ', 'T') + 'Z');
}

/** Eingabetyp aller Formatierer — deckt die nullable Felder der API mit ab. */
type DateInput = string | number | Date | null | undefined;

/**
 * Format date + time: "15.01.2024, 10:00"
 */
export function formatDateTime(dateString: DateInput): string {
  if (!dateString) return '-';
  return parseUTC(dateString).toLocaleDateString(LOCALE, {
    timeZone: getDisplayTimezone(),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date + time with seconds: "15.01.2024, 10:00:30"
 */
export function formatDateTimeFull(dateString: DateInput): string {
  if (!dateString) return '-';
  return parseUTC(dateString).toLocaleString(LOCALE, {
    timeZone: getDisplayTimezone(),
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Format date only: "15.01.2024"
 */
export function formatDate(dateString: DateInput): string {
  if (!dateString) return '-';
  return parseUTC(dateString).toLocaleDateString(LOCALE, {
    timeZone: getDisplayTimezone(),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format time only: "10:00"
 */
export function formatTime(dateString: DateInput): string {
  if (!dateString) return '-';
  return parseUTC(dateString).toLocaleTimeString(LOCALE, {
    timeZone: getDisplayTimezone(),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Reines Datumsfeld (`YYYY-MM-DD`, z. B. MHD, Liefer-, Rechnungsdatum) formatieren.
 *
 * ⚠️ NIEMALS über parseUTC: Ein Datum ohne Uhrzeit hat keine Zeitzone. Würde man es als
 * UTC-Mitternacht lesen und in die Zone des Betrachters rechnen, spränge es bei negativen
 * Offsets (Amerika) auf den Vortag — ein MHD wäre dann einen Tag zu früh.
 * Deshalb wird der String zerlegt, nicht umgerechnet.
 */
export function formatDateOnly(dateString: DateInput): string {
  if (!dateString) return '-';
  const match = typeof dateString === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString) : null;
  if (!match) return formatDate(dateString);
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

/**
 * Frei formatieren mit eigenen Intl-Optionen — für Anzeigen, die mehr wollen als die
 * Standardformate (Wochentag, Monatsname, nur Stunde …). Rechnet wie alle anderen Helfer
 * den UTC-Zeitstempel in die Zeitzone des Betrachters.
 *
 * @param kind Welche Intl-Methode: 'date' (Standard), 'time' oder 'datetime'
 */
export function formatCustom(
  dateString: DateInput,
  options: Intl.DateTimeFormatOptions,
  kind: 'date' | 'time' | 'datetime' = 'date',
): string {
  if (!dateString) return '-';
  const date = parseUTC(dateString);
  const opts = { timeZone: getDisplayTimezone(), ...options };
  if (kind === 'time') return date.toLocaleTimeString(LOCALE, opts);
  if (kind === 'datetime') return date.toLocaleString(LOCALE, opts);
  return date.toLocaleDateString(LOCALE, opts);
}

/**
 * Format date with weekday: "Montag, 15. Januar 2024"
 */
export function formatDateLong(dateString: DateInput): string {
  if (!dateString) return '-';
  return parseUTC(dateString).toLocaleDateString(LOCALE, {
    timeZone: getDisplayTimezone(),
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Kalendertag eines Zeitstempels in der Anzeigezone als Schlüssel "YYYY-MM-DD" — für „heute /
 * gestern“-Vergleiche und Gruppierungen (Postfach-Liste). Nie über getDate()/getMonth() des
 * Betrachter-Rechners vergleichen, sonst stimmt die Gruppe nicht mit der angezeigten Uhrzeit.
 */
export function kalendertagSchluessel(dateString: DateInput): string {
  if (!dateString) return '';
  return parseUTC(dateString).toLocaleDateString('en-CA', {
    timeZone: getDisplayTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Lokaler Kalendertag eines vom Benutzer gewählten `Date` (lokale Mitternacht) als "YYYY-MM-DD" —
 * für Zeiträume, die als Datum an die API gehen. NICHT `toISOString()`: das rechnet nach UTC und
 * schiebt lokale Mitternacht östlich von UTC (DE/AT/NL/TH) auf den Vortag.
 */
export function formatLocalDateKey(date: Date): string {
  const monat = String(date.getMonth() + 1).padStart(2, '0');
  const tag = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${monat}-${tag}`;
}

/**
 * Monatsname in der Anzeigezone, in der Sprache der Oberfläche: "September".
 */
export function monatsName(dateString: DateInput, sprache: string = 'de'): string {
  if (!dateString) return '';
  return parseUTC(dateString).toLocaleDateString(sprache, {
    timeZone: getDisplayTimezone(),
    month: 'long',
  });
}

/**
 * Datum für eine kompakte Listenzeile (Outlook-/Gmail-Muster): heute → Uhrzeit "06:45", dieses
 * Jahr → Tag + Monat "6. Okt.", früher → "21.07.2026".
 */
export function formatListenDatum(dateString: DateInput, sprache: string = 'de'): string {
  if (!dateString) return '';
  const tag = kalendertagSchluessel(dateString);
  const heute = kalendertagSchluessel(Date.now());
  if (tag === heute) return formatTime(dateString);
  if (tag.slice(0, 4) === heute.slice(0, 4)) {
    return parseUTC(dateString).toLocaleDateString(sprache, {
      timeZone: getDisplayTimezone(),
      day: 'numeric',
      month: 'short',
    });
  }
  return formatDate(dateString);
}
